import { useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import type { Brand } from '../types';

export function SettingsPage() {
  const [crmApiKey, setCrmApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Notification / Resend state
  const [resendApiKey, setResendApiKey] = useState('');
  const [resendKeyIsSet, setResendKeyIsSet] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState('');
  const [savingResendKey, setSavingResendKey] = useState(false);
  const [savingEmails, setSavingEmails] = useState(false);
  const [testingResend, setTestingResend] = useState(false);
  const [resendTestResult, setResendTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [resendKeySaved, setResendKeySaved] = useState(false);
  const [emailsSaved, setEmailsSaved] = useState(false);

  // AI state
  const [aiProvider, setAiProvider] = useState('anthropic');
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiKeyIsSet, setAiKeyIsSet] = useState(false);
  const [aiModel, setAiModel] = useState('claude-haiku-4-5-20251001');
  const [savingAi, setSavingAi] = useState(false);
  const [aiSaved, setAiSaved] = useState(false);
  const [testingAi, setTestingAi] = useState(false);
  const [aiTestResult, setAiTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // GitHub / site rebuild state
  const [githubToken, setGithubToken] = useState('');
  const [githubTokenIsSet, setGithubTokenIsSet] = useState(false);
  const [githubOwner, setGithubOwner] = useState('');
  const [githubRepo, setGithubRepo] = useState('');
  const [savingGithub, setSavingGithub] = useState(false);
  const [githubSaved, setGithubSaved] = useState(false);

  // Google Analytics coverage (configured per brand)
  const [brands, setBrands] = useState<Brand[]>([]);

  useEffect(() => {
    fetchCrmApiKey();
    fetchResendSettings();
    fetchAiSettings();
    fetchGithubSettings();
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await fetch(getApiUrl('/api/brands'), { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setBrands(data.brands || []);
      }
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    }
  };

  const fetchGithubSettings = async () => {
    try {
      const [tokenRes, ownerRes, repoRes] = await Promise.all([
        fetch(getApiUrl('/api/settings/github_token'), { credentials: 'include' }),
        fetch(getApiUrl('/api/settings/github_owner'), { credentials: 'include' }),
        fetch(getApiUrl('/api/settings/github_repo'), { credentials: 'include' }),
      ]);
      if (tokenRes.ok) {
        const d = await tokenRes.json();
        setGithubTokenIsSet(!!d.setting.value);
        setGithubToken(d.setting.value || '');
      }
      if (ownerRes.ok) {
        const d = await ownerRes.json();
        setGithubOwner(d.setting.value || '');
      }
      if (repoRes.ok) {
        const d = await repoRes.json();
        setGithubRepo(d.setting.value || '');
      }
    } catch (error) {
      console.error('Failed to fetch GitHub settings:', error);
    }
  };

  const handleSaveGithub = async () => {
    setSavingGithub(true);
    setGithubSaved(false);
    try {
      const updates: Promise<Response>[] = [
        fetch(getApiUrl('/api/settings/github_owner'), {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ value: githubOwner }),
        }),
        fetch(getApiUrl('/api/settings/github_repo'), {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ value: githubRepo }),
        }),
      ];
      // Only update the token if the user typed a new one (not the masked placeholder)
      if (githubToken && !githubToken.includes('•')) {
        updates.push(fetch(getApiUrl('/api/settings/github_token'), {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ value: githubToken }),
        }));
      }
      await Promise.all(updates);
      setGithubSaved(true);
      fetchGithubSettings();
      setTimeout(() => setGithubSaved(false), 4000);
    } catch (error) {
      alert('Failed to save GitHub settings');
    } finally {
      setSavingGithub(false);
    }
  };

  const fetchCrmApiKey = async () => {
    try {
      const response = await fetch(getApiUrl('/api/settings/crm_api_key'), {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        // Will show masked value if set
        setCrmApiKey(data.setting.value || '');
      }
    } catch (error) {
      console.error('Failed to fetch CRM API key:', error);
    }
  };

  const handleSaveCrmApiKey = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      const response = await fetch(getApiUrl('/api/settings/crm_api_key'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: crmApiKey }),
      });

      if (response.ok) {
        alert('CRM API key saved successfully!');
        fetchCrmApiKey(); // Refresh to show masked value
      } else {
        const data = await response.json();
        alert(`Failed to save: ${data.error}`);
      }
    } catch (error) {
      alert('Failed to save CRM API key');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await fetch(getApiUrl('/api/settings/test-crm'), {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();
      setTestResult({
        success: data.success,
        message: data.success ? data.message : data.error
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Failed to test connection'
      });
    } finally {
      setTesting(false);
    }
  };

  const fetchResendSettings = async () => {
    try {
      const [keyRes, emailsRes] = await Promise.all([
        fetch(getApiUrl('/api/settings/resend_api_key'), { credentials: 'include' }),
        fetch(getApiUrl('/api/settings/notification_emails'), { credentials: 'include' }),
      ]);
      if (keyRes.ok) {
        const data = await keyRes.json();
        // Never pre-fill the input with the masked value — that would cause
        // the placeholder characters to be saved back on the next Save click.
        // Instead, track whether a key is already stored and keep input blank.
        const isAlreadySet = !!(data.setting.value);
        setResendKeyIsSet(isAlreadySet);
        setResendApiKey('');
      }
      if (emailsRes.ok) {
        const data = await emailsRes.json();
        setNotificationEmails(data.setting.value || '');
      }
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    }
  };

  const handleSaveResendKey = async () => {
    setSavingResendKey(true);
    setResendTestResult(null);
    setResendKeySaved(false);
    try {
      const response = await fetch(getApiUrl('/api/settings/resend_api_key'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: resendApiKey }),
      });
      if (response.ok) {
        setResendKeySaved(true);
        fetchResendSettings();
        setTimeout(() => setResendKeySaved(false), 3000);
      } else {
        const data = await response.json();
        alert(`Failed to save: ${data.error}`);
      }
    } catch {
      alert('Failed to save Resend API key');
    } finally {
      setSavingResendKey(false);
    }
  };

  const handleSaveEmails = async () => {
    setSavingEmails(true);
    setEmailsSaved(false);
    try {
      const response = await fetch(getApiUrl('/api/settings/notification_emails'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: notificationEmails }),
      });
      if (response.ok) {
        setEmailsSaved(true);
        setTimeout(() => setEmailsSaved(false), 3000);
      } else {
        const data = await response.json();
        alert(`Failed to save: ${data.error}`);
      }
    } catch {
      alert('Failed to save notification emails');
    } finally {
      setSavingEmails(false);
    }
  };

  const handleTestResend = async () => {
    setTestingResend(true);
    setResendTestResult(null);
    try {
      const response = await fetch(getApiUrl('/api/settings/test-resend'), {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      setResendTestResult({
        success: data.success,
        message: data.success ? data.message : data.error,
      });
    } catch {
      setResendTestResult({ success: false, message: 'Failed to test connection' });
    } finally {
      setTestingResend(false);
    }
  };

  const fetchAiSettings = async () => {
    try {
      const keys = ['ai_provider', 'ai_api_key', 'ai_model'];
      const results = await Promise.all(
        keys.map(k => fetch(getApiUrl(`/api/settings/${k}`), { credentials: 'include' }).then(r => r.ok ? r.json() : null))
      );
      const vals: Record<string, string> = {};
      results.forEach((r, i) => { if (r?.setting?.value !== undefined) vals[keys[i]] = r.setting.value; });
      if (vals.ai_provider) setAiProvider(vals.ai_provider);
      setAiKeyIsSet(!!vals.ai_api_key);
      setAiApiKey('');
      if (vals.ai_model) setAiModel(vals.ai_model);
    } catch {}
  };

  const saveAiSetting = async (key: string, value: string) => {
    setSavingAi(true);
    setAiSaved(false);
    try {
      const res = await fetch(getApiUrl(`/api/settings/${key}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value }),
      });
      if (res.ok) {
        setAiSaved(true);
        if (key === 'ai_api_key') fetchAiSettings();
        setTimeout(() => setAiSaved(false), 3000);
      } else {
        const data = await res.json();
        alert(`Failed to save: ${data.error}`);
      }
    } catch {
      alert('Failed to save setting');
    } finally {
      setSavingAi(false);
    }
  };

  const handleTestAi = async () => {
    setTestingAi(true);
    setAiTestResult(null);
    try {
      const res = await fetch(getApiUrl('/api/settings/test-ai'), { method: 'POST', credentials: 'include' });
      const data = await res.json();
      setAiTestResult({ success: data.success, message: data.success ? data.message : data.error });
    } catch {
      setAiTestResult({ success: false, message: 'Failed to test connection' });
    } finally {
      setTestingAi(false);
    }
  };

  return (
    <div>
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Settings</h1>
        <p className="text-dark-text-secondary text-sm sm:text-base">Manage system configuration and integrations</p>
      </div>

      <div className="grid gap-6">
        {/* GitHub / Site Rebuild Settings */}
        <div className="card p-6">
          <h2 className="text-xl font-bold text-white mb-2">Site Rebuild (GitHub)</h2>
          <p className="text-dark-text-secondary mb-6">
            The public sites are statically built. Product and article changes go live on the
            nightly rebuild, or instantly via the "Rebuild Sites" button on the Products page.
            That button needs a GitHub token with permission to trigger the rebuild workflow.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">GitHub Token</label>
              <input
                type="text"
                value={githubToken}
                onChange={(e) => setGithubToken(e.target.value)}
                placeholder={githubTokenIsSet ? '•••••••• (saved — type to replace)' : 'github_pat_... or ghp_...'}
                className="input w-full font-mono text-sm"
              />
              <p className="text-xs text-dark-text-secondary mt-1">
                Fine-grained token with <span className="font-mono">Contents: write</span> (or a
                classic token with <span className="font-mono">repo</span> scope) on this repository.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">Repository Owner</label>
                <input
                  type="text"
                  value={githubOwner}
                  onChange={(e) => setGithubOwner(e.target.value)}
                  placeholder="alexanderashe93"
                  className="input w-full font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">Repository Name</label>
                <input
                  type="text"
                  value={githubRepo}
                  onChange={(e) => setGithubRepo(e.target.value)}
                  placeholder="MPSLeadGen"
                  className="input w-full font-mono text-sm"
                />
              </div>
            </div>

            {githubSaved && (
              <div className="rounded-lg p-4 bg-green-900/30 border border-green-700">
                <p className="text-sm font-medium text-green-400">✓ GitHub settings saved</p>
              </div>
            )}

            <button
              onClick={handleSaveGithub}
              disabled={savingGithub}
              className="btn btn-primary"
            >
              {savingGithub ? 'Saving...' : 'Save GitHub Settings'}
            </button>
          </div>
        </div>

        {/* Google Analytics coverage */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-white">Google Analytics</h2>
            <span className="text-sm text-dark-text-secondary">
              {brands.filter(b => b.google_analytics_id).length}/{brands.length} sites configured
            </span>
          </div>
          <p className="text-dark-text-secondary mb-6">
            Each brand's measurement ID is set on its Brand Settings page.
          </p>

          {brands.length === 0 ? (
            <p className="text-dark-text-secondary text-center py-4">Loading...</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {brands.map((brand) => {
                const hasGA = !!brand.google_analytics_id;
                return (
                  <div
                    key={brand.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      hasGA
                        ? 'border-green-800 bg-green-900/10'
                        : 'border-dark-border bg-dark-bg'
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{brand.name}</p>
                      <p className="text-xs text-dark-text-secondary truncate">
                        {hasGA ? brand.google_analytics_id : brand.domain}
                      </p>
                    </div>
                    <span className={`ml-3 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      hasGA ? 'bg-green-600 text-white' : 'bg-dark-border text-dark-text-secondary'
                    }`}>
                      {hasGA ? '✓' : '–'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* CRM Integration Settings */}
        <div className="card p-6">
          <h2 className="text-xl font-bold text-white mb-2">CRM Integration</h2>
          <p className="text-dark-text-secondary mb-6">
            Configure the Britannia CRM API connection for transferring leads
          </p>

          <div className="space-y-4">
            {/* API Key Input */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                CRM API Key
              </label>
              <input
                type="text"
                value={crmApiKey}
                onChange={(e) => setCrmApiKey(e.target.value)}
                placeholder="bitscrm_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="input w-full font-mono text-sm"
              />
              <p className="text-xs text-dark-text-secondary mt-1">
                Format: bitscrm_... • Get your API key from{' '}
                <a
                  href="https://crm.britannia.tools"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  Britannia CRM
                </a>
              </p>
            </div>

            {/* Test Result */}
            {testResult && (
              <div
                className={`rounded-lg p-4 ${
                  testResult.success
                    ? 'bg-green-900/30 border border-green-700'
                    : 'bg-red-900/30 border border-red-700'
                }`}
              >
                <p
                  className={`text-sm font-medium ${
                    testResult.success ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {testResult.success ? '✓ ' : '⚠ '}
                  {testResult.message}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleSaveCrmApiKey}
                disabled={saving || !crmApiKey}
                className="btn btn-primary"
              >
                {saving ? 'Saving...' : 'Save API Key'}
              </button>
              <button
                onClick={handleTestConnection}
                disabled={testing}
                className="btn btn-secondary"
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-6 bg-dark-bg rounded-lg p-4">
            <h3 className="text-sm font-bold text-white mb-2">How to get your API key:</h3>
            <ol className="text-sm text-dark-text-secondary space-y-1 list-decimal list-inside">
              <li>Log in to the <a href="https://crm.britannia.tools" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Britannia CRM</a></li>
              <li>Navigate to Settings → API Keys</li>
              <li>Create a new API key with "leads:write" and "leads:read" permissions</li>
              <li>Copy the key (it starts with "bitscrm_") and paste it above</li>
              <li>Click "Save API Key" and then "Test Connection" to verify</li>
            </ol>
          </div>
        </div>

        {/* Profile Settings */}
        <div className="card p-6">
          <h2 className="text-xl font-bold text-white mb-4">Profile</h2>
          <p className="text-dark-text-secondary mb-6">
            Update your name, email, and profile picture
          </p>
          <p className="text-sm text-dark-text-secondary">
            Coming soon - profile editing functionality
          </p>
        </div>

        {/* Security Settings */}
        <div className="card p-6">
          <h2 className="text-xl font-bold text-white mb-4">Security</h2>
          <p className="text-dark-text-secondary mb-6">
            Manage your password and enable two-factor authentication
          </p>
          <p className="text-sm text-dark-text-secondary">
            Coming soon - password change and 2FA setup
          </p>
        </div>

        {/* AI Integration */}
        <div className="card p-6">
          <h2 className="text-xl font-bold text-white mb-2">AI Integration</h2>
          <p className="text-dark-text-secondary mb-6">
            Configure the AI provider used for product research on the Products page.
          </p>

          <div className="space-y-6">
            {/* Provider */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-1">AI Provider</label>
              <div className="flex gap-3">
                <select
                  value={aiProvider}
                  onChange={e => setAiProvider(e.target.value)}
                  className="input text-sm"
                >
                  <option value="anthropic">Anthropic (Claude)</option>
                  <option value="openai">OpenAI (GPT)</option>
                </select>
                <button
                  onClick={() => saveAiSetting('ai_provider', aiProvider)}
                  disabled={savingAi}
                  className="btn btn-secondary text-sm"
                >
                  Save
                </button>
              </div>
            </div>

            {/* Model */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-1">Model</label>
              <div className="flex gap-3">
                <input
                  value={aiModel}
                  onChange={e => setAiModel(e.target.value)}
                  className="input flex-1 font-mono text-sm"
                  placeholder={aiProvider === 'anthropic' ? 'claude-haiku-4-5-20251001' : 'gpt-4.1-mini'}
                />
                <button
                  onClick={() => saveAiSetting('ai_model', aiModel)}
                  disabled={savingAi || !aiModel.trim()}
                  className="btn btn-secondary text-sm"
                >
                  Save
                </button>
              </div>
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-1">API Key</label>
              <div className="space-y-2">
                <input
                  type="password"
                  value={aiApiKey}
                  onChange={e => setAiApiKey(e.target.value)}
                  placeholder={aiKeyIsSet ? 'Enter a new key to replace the saved one' : 'sk-... or anthropic key'}
                  className="input w-full font-mono text-sm"
                />
                {aiKeyIsSet && !aiApiKey && (
                  <p className="text-xs text-green-500">API key is saved</p>
                )}

                {aiTestResult && (
                  <div className={`rounded-lg p-3 ${aiTestResult.success ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'}`}>
                    <p className={`text-sm font-medium ${aiTestResult.success ? 'text-green-400' : 'text-red-400'}`}>
                      {aiTestResult.success ? '\u2713 ' : '\u26a0 '}{aiTestResult.message}
                    </p>
                  </div>
                )}

                {aiSaved && <p className="text-sm text-green-400 font-medium">Saved successfully</p>}

                <div className="flex gap-3">
                  <button
                    onClick={() => saveAiSetting('ai_api_key', aiApiKey)}
                    disabled={savingAi || !aiApiKey.trim()}
                    className="btn btn-primary"
                  >
                    {savingAi ? 'Saving...' : 'Save API Key'}
                  </button>
                  <button onClick={handleTestAi} disabled={testingAi} className="btn btn-secondary">
                    {testingAi ? 'Testing...' : 'Test Connection'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="card p-6">
          <h2 className="text-xl font-bold text-white mb-2">Email Notifications</h2>
          <p className="text-dark-text-secondary mb-6">
            Configure Resend to send instant lead alerts to your team whenever a new enquiry comes in
          </p>

          <div className="space-y-8">
            {/* Resend API Key */}
            <div>
              <h3 className="text-base font-semibold text-white mb-1">Resend API Key</h3>
              <p className="text-xs text-dark-text-secondary mb-3">
                Your API key is stored securely and never exposed in plain text after saving.
              </p>
              <div className="space-y-3">
                <input
                  type="password"
                  value={resendApiKey}
                  onChange={(e) => setResendApiKey(e.target.value)}
                  placeholder={resendKeyIsSet ? 'Enter a new key to replace the saved one' : 're_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
                  className="input w-full font-mono text-sm"
                />
                {resendKeyIsSet && !resendApiKey && (
                  <p className="text-xs text-green-500">✓ API key is saved — enter a new key above to replace it</p>
                )}

                {/* Resend test result */}
                {resendTestResult && (
                  <div
                    className={`rounded-lg p-4 ${
                      resendTestResult.success
                        ? 'bg-green-900/30 border border-green-700'
                        : 'bg-red-900/30 border border-red-700'
                    }`}
                  >
                    <p className={`text-sm font-medium ${resendTestResult.success ? 'text-green-400' : 'text-red-400'}`}>
                      {resendTestResult.success ? '✓ ' : '⚠ '}
                      {resendTestResult.message}
                    </p>
                  </div>
                )}

                {resendKeySaved && (
                  <p className="text-sm text-green-400 font-medium">✓ API key saved successfully</p>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleSaveResendKey}
                    disabled={savingResendKey || !resendApiKey.trim()}
                    className="btn btn-primary"
                  >
                    {savingResendKey ? 'Saving...' : 'Save API Key'}
                  </button>
                  <button
                    onClick={handleTestResend}
                    disabled={testingResend}
                    className="btn btn-secondary"
                  >
                    {testingResend ? 'Testing...' : 'Test Connection'}
                  </button>
                </div>
              </div>

              <div className="mt-4 bg-dark-bg rounded-lg p-4">
                <h4 className="text-sm font-bold text-white mb-2">How to get your Resend API key:</h4>
                <ol className="text-sm text-dark-text-secondary space-y-1 list-decimal list-inside">
                  <li>Log in to <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">resend.com</a></li>
                  <li>Go to API Keys and create a new key with "Sending access"</li>
                  <li>Ensure the domain <span className="font-mono text-dark-text">leadgen.britannia.tools</span> is verified in Resend</li>
                  <li>Paste the key above and click "Save API Key", then "Test Connection"</li>
                </ol>
              </div>
            </div>

            <div className="border-t border-dark-border" />

            {/* Notification Recipients */}
            <div>
              <h3 className="text-base font-semibold text-white mb-1">Notification Recipients</h3>
              <p className="text-xs text-dark-text-secondary mb-3">
                These email addresses will receive an alert every time a new lead is submitted. Separate multiple addresses with a comma.
              </p>
              <div className="space-y-3">
                <input
                  type="text"
                  value={notificationEmails}
                  onChange={(e) => setNotificationEmails(e.target.value)}
                  placeholder="alex@britanniait.co.uk, frank@britanniait.co.uk"
                  className="input w-full text-sm"
                />

                {emailsSaved && (
                  <p className="text-sm text-green-400 font-medium">✓ Recipients saved successfully</p>
                )}

                <button
                  onClick={handleSaveEmails}
                  disabled={savingEmails || !notificationEmails}
                  className="btn btn-primary"
                >
                  {savingEmails ? 'Saving...' : 'Save Recipients'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
